import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "../../../../../components/ui/alert";
import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { Textarea } from "../../../../../components/ui/textarea";
import { OutcomeType } from "../../../../../models/audit/attributeTestColumn";
import { OUTCOME_OPTIONS } from "./outcomeOptions";
import { type AnalyticsOverrideFlow } from "../../../../../hooks/audit/useAnalyticsOverrideFlow";

// The notice announces itself — `Alert` carries `role="alert"`, so it is read
// out the moment it appears, which is the whole of the announcement. The id is
// for the association instead: `aria-describedby` on the disabled Save is what
// lets a reader moving a virtual cursor over the dialog find the explanation
// for a button that cannot be pressed, since a natively disabled button is out
// of the tab order and never takes focus to describe itself.
const SUPERSEDED_NOTICE_ID = "analytics-override-superseded";

/**
 * The dialog that collects an auditor's own conclusion for an analytics cell.
 *
 * It collects rather than decides: every rule about what may be sent and what
 * happens to the answer lives in `useAnalyticsOverrideFlow`, which both the
 * analytics sheet and the work-paper fold-out drive. Rendering it is all a host
 * has to do to offer the override.
 *
 * Hosts mount it beside their own content rather than inside it. A refetch
 * flips the cache entry to loading and notifies synchronously, so a dialog
 * mounted inside a host's loading and error states would be torn down for the
 * length of the superseded path's re-read, taking the auditor's typed
 * conclusion and their caret with it, and would never come back if that re-read
 * failed.
 */
export function AnalyticsOverrideDialog({
  flow,
}: {
  flow: AnalyticsOverrideFlow;
}) {
  // A string in every state, including no outcome chosen. What suppresses the
  // trigger's mirror of the selected item is the value having children at all,
  // so resolving to `undefined` here would quietly restore it.
  const selectedOutcomeLabel =
    OUTCOME_OPTIONS.find((option) => option.value === flow.outcome)?.label ??
    "";

  return (
    <Dialog
      open={flow.isOpen}
      onOpenChange={(open) => (open ? undefined : flow.close())}
    >
      {/* The shared `DialogContent` centers on the viewport and caps no
          height of its own, so on a short one the population field and the
          superseded notice together push the footer past the bottom edge —
          unreachable in exactly the state whose notice tells the auditor to
          reach Cancel. Capped on this dialog rather than on the primitive,
          which other dialogs take their base sizing from and override where
          their own content needs it.

          The work-paper fold-out this dialog opens over closes on any Escape
          that reaches the window. Radix handles Escape for the dialog, and
          for the outcome list inside it, from a capture-phase listener on the
          document, so stopping the press there keeps it from the fold-out.
          With the outcome list open, Escape closes only the list, and the
          dialog and its typed conclusion survive. */}
      <DialogContent
        className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>New Conclusion</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="analytics-outcome">
              Outcome{" "}
              <span aria-hidden="true" className="text-red-500">
                *
              </span>
            </Label>
            {/* The empty string is the unchosen value rather than
                `undefined`, which Radix reads as "uncontrolled" and would
                have the select switch modes the moment an outcome is picked.
                Both render the placeholder. */}
            <Select
              value={flow.outcome ?? ""}
              onValueChange={(value) =>
                flow.setOutcome(OutcomeType.parse(value))
              }
            >
              <SelectTrigger
                aria-required
                className="max-w-md"
                id="analytics-outcome"
              >
                {/* The trigger states the chosen answer itself. Left to render
                    the selected item, it would mirror that item's whole
                    content — description included — into a single-line,
                    fixed-height trigger; naming the label here is what keeps
                    the explanatory line to the list it explains. */}
                <SelectValue placeholder="Select an outcome">
                  {selectedOutcomeLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent onEscapeKeyDown={(e) => e.stopPropagation()}>
                {OUTCOME_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    // Typeahead matches an option by its rendered text unless
                    // given one, and this option's rendered text is the
                    // description as well as the answer.
                    textValue={option.label}
                  >
                    <span className="block font-medium">{option.label}</span>
                    <span className="block text-xs text-font-secondary mt-0.5">
                      {option.description}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-sm text-font-secondary">
              Your answer replaces the current one; the answer it replaces is
              kept.
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="analytics-conclusion">
              Conclusion{" "}
              <span aria-hidden="true" className="text-red-500">
                *
              </span>
            </Label>
            <Textarea
              id="analytics-conclusion"
              aria-required
              placeholder="Enter your conclusion for this cell..."
              value={flow.conclusion}
              onChange={(e) => flow.setConclusion(e.target.value)}
              rows={4}
            />
          </div>

          {flow.populationPrompted && (
            <div className="grid gap-2">
              <Label htmlFor="analytics-population">
                Population Tested
                {flow.populationRequired && (
                  <span aria-hidden="true" className="text-red-500">
                    {" "}
                    *
                  </span>
                )}
              </Label>
              <Input
                id="analytics-population"
                aria-required={flow.populationRequired}
                type="number"
                min={0}
                step={1}
                className="max-w-md"
                placeholder="Number of rows tested"
                value={flow.populationTotal}
                onChange={(e) => flow.setPopulationTotal(e.target.value)}
              />
              <div className="text-sm text-font-secondary">
                {flow.populationRequired
                  ? "This cell counts no population and no run resolved one, so a conclusion needs a denominator."
                  : "Leave blank to use the figure this cell's run resolved; if the run resolved none, enter the population you tested."}
              </div>
            </div>
          )}
        </div>
        {flow.overrideSuperseded && (
          <Alert
            id={SUPERSEDED_NOTICE_ID}
            className="bg-amber-50 border-amber-200"
          >
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              A newer conclusion replaced the one you were answering, so this
              text cannot be saved against it. Copy your text, then close this
              panel and reopen the cell to start a new conclusion.
            </AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={flow.close}
            disabled={flow.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={flow.submit}
            disabled={!flow.canSubmit || flow.isSubmitting}
            aria-describedby={
              flow.overrideSuperseded ? SUPERSEDED_NOTICE_ID : undefined
            }
          >
            {flow.isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
